import { useEffect, useRef } from "react";

import { datadogRum } from "@datadog/browser-rum";
import { reactPlugin } from "@datadog/browser-rum-react";
import LogRocket from "logrocket";
import { validate as validateUUID } from "uuid";

import { useUser } from "src/contexts/user-context";
import { currentBuildSha } from "src/lib/chunk-reload";
import { monitoringEnvironment } from "src/lib/monitoring-env";
import { auditEntries } from "src/utils/storage";

const excludedPatterns = [
  /launchdarkly\.com/,
  /sentry\.io/,
  /hightouch-events\.com/,
  /logrocket\.com/,
  /getkoala\.com/,
  /usepylon\.com/,
  /status\.hightouch\.io/,
];

/** Matches app GraphQL HTTP endpoint across regional API hosts. */
const GRAPHQL_HTTP_ENDPOINT = /\/v1\/graphql(?:\?.*)?$/i;

/**
 * Injecting trace headers makes a fetch non-simple for CORS, forcing an
 * OPTIONS preflight per request. Agent-file requests (`/agents/files`,
 * `/ad-studio/agents/files`) are high-volume CDN media fetches, so skip
 * tracing for them.
 */
export function shouldTraceRequest(url: string): boolean {
  return (
    /https:\/\/.*\.hightouch\.com/.test(url) && !url.includes("/agents/files")
  );
}

const WORKSPACE_SLUG_PREFIX = /^\/:root_workspace_slug(?=\/|$)/;
const NUMERIC_ID_SEGMENT = /^\d{2,}(-\d{2,})*$/;

/**
 * Normalize an `@view.name` to a stable aggregate route. Strips the
 * `/:root_workspace_slug` prefix and replaces UUID / multi-digit numeric
 * segments with `:id` so 404s on stale URLs and any future route that
 * captures a raw identifier don't fan out into thousands of unique view
 * names. Idempotent on canonical routes (`:id`, `:sync_id`, etc).
 */
export function normalizeRoutePath(name: string): string {
  if (!name) return name;
  const withoutWorkspace = name.replace(WORKSPACE_SLUG_PREFIX, "");
  const path = withoutWorkspace === "" ? "/" : withoutWorkspace;
  return path
    .split("/")
    .map((segment) => {
      if (!segment) return segment;
      if (validateUUID(segment)) return ":id";
      if (NUMERIC_ID_SEGMENT.test(segment)) return ":id";
      return segment;
    })
    .join("/");
}

/** Manual RUM action for refresh spans (distinct from auto click actions). */
export const REFRESH_RESULTS_RUM_ACTION = "audience:refresh-results";

/**
 * Wraps async refresh work in a Datadog RUM action so polls and errors attach to
 * one action waterfall (start/stop span, not click idle-time heuristics).
 */
export async function withRumRefreshAction<T>(
  fn: () => Promise<T>,
  context?: Record<string, unknown>,
): Promise<T> {
  const actionKey = crypto.randomUUID();
  datadogRum.startAction(REFRESH_RESULTS_RUM_ACTION, { actionKey, context });
  try {
    return await fn();
  } finally {
    datadogRum.stopAction(REFRESH_RESULTS_RUM_ACTION, { actionKey });
  }
}

/**
 * Spans a Datadog RUM action across an external `isLoading` flag. Call the
 * returned `start` once work will actually run (e.g. via `onQueryStart` on
 * `calculateSizes`); the action stops when `isLoading` flips false.
 */
export function useRumRefreshAction(
  isLoading: boolean,
  context?: Record<string, unknown>,
) {
  const actionKeyRef = useRef<string | null>(null);
  const wasLoadingRef = useRef(isLoading);
  const contextRef = useRef(context);
  contextRef.current = context;

  const stopOpenAction = () => {
    if (!actionKeyRef.current) {
      return;
    }
    datadogRum.stopAction(REFRESH_RESULTS_RUM_ACTION, {
      actionKey: actionKeyRef.current,
    });
    actionKeyRef.current = null;
  };

  // handle happy path action resolution
  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (wasLoadingRef.current && !isLoading) {
      stopOpenAction();
    }
    wasLoadingRef.current = isLoading;
  }, [isLoading]);

  // handle unmount
  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    return () => {
      stopOpenAction();
    };
  }, []);

  const startRefreshAction = () => {
    stopOpenAction();
    const actionKey = crypto.randomUUID();
    actionKeyRef.current = actionKey;
    datadogRum.startAction(REFRESH_RESULTS_RUM_ACTION, {
      actionKey,
      context: contextRef.current,
    });
  };

  return startRefreshAction;
}

let firstGqlViewId: string | undefined;

export function trackFirstGraphqlResponse(viewId: string): void {
  if (viewId !== firstGqlViewId) {
    datadogRum.addTiming("first_graphql_response");
    firstGqlViewId = viewId;
  }
}

export function resetFirstGqlTracking(): void {
  firstGqlViewId = undefined;
}

export function shouldInitDatadogRum({
  prod,
  vercelEnv,
}: {
  prod: boolean;
  vercelEnv: string | undefined;
}): boolean {
  return prod && vercelEnv !== "development";
}

if (
  shouldInitDatadogRum({
    prod: import.meta.env.PROD,
    vercelEnv: import.meta.env.VITE_VERCEL_ENV,
  })
) {
  datadogRum.init({
    applicationId: import.meta.env.VITE_DATADOG_APPLICATION_ID,
    clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
    site: "datadoghq.com",
    service: "app",
    env: monitoringEnvironment(import.meta.env.VITE_VERCEL_ENV),
    version: currentBuildSha(),
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0,
    defaultPrivacyLevel: "mask-user-input",
    allowedTracingUrls: [shouldTraceRequest],
    allowedGraphQlUrls: [
      {
        match: GRAPHQL_HTTP_ENDPOINT,
        trackPayload: false,
        trackResponseErrors: true,
      },
    ],
    excludedActivityUrls: excludedPatterns,
    trackUserInteractions: true,
    enableExperimentalFeatures: ["start_stop_action"],
    plugins: [reactPlugin({ router: true })],
    beforeSend: (event) => {
      if (event.type === "view") {
        event.view.name = normalizeRoutePath(event.view.name ?? "");
      }
      if (event.type === "resource") {
        const url = event.resource.url.toLowerCase();
        const isExcluded = excludedPatterns.some((pattern) =>
          pattern.test(url),
        );
        if (isExcluded) {
          return false;
        }

        if (GRAPHQL_HTTP_ENDPOINT.test(url)) {
          trackFirstGraphqlResponse(event.view.id);
        }
      }
      return true;
    },
  });

  datadogRum.setGlobalContextProperty(
    "vercel_env",
    import.meta.env.VITE_VERCEL_ENV ?? "unknown",
  );

  // Tag every RUM event with the LogRocket session URL so we can pivot from
  // a Datadog error/view to the matching session replay. Sentry does the same
  // via beforeSend in sentry.tsx.
  LogRocket.getSessionURL((sessionURL) => {
    datadogRum.setGlobalContextProperty("logrocket_session", sessionURL);
  });
}

let auditedThisSession = false;

/**
 * One-shot at session start: report the localStorage footprint so we can see
 * which keys/prefixes drive users toward the browser quota. Sends sizes only
 * — never values — because drafts (e.g. unsaved-sql-*) may contain customer
 * data.
 */
function reportLocalStorageAudit(): void {
  const entries = auditEntries();
  if (entries.length === 0) return;

  let totalBytes = 0;
  const bytesByPrefix: Record<string, { keys: number; bytes: number }> = {};

  for (const { key, bytes } of entries) {
    totalBytes += bytes;
    const prefix = bucketPrefixForKey(key);
    let bucket = bytesByPrefix[prefix];
    if (!bucket) {
      bucket = { keys: 0, bytes: 0 };
      bytesByPrefix[prefix] = bucket;
    }
    bucket.keys += 1;
    bucket.bytes += bytes;
  }

  datadogRum.addAction("storage:audit", {
    total_bytes: totalBytes,
    key_count: entries.length,
    bytes_by_prefix: bytesByPrefix,
  });
}

/**
 * Group localStorage keys into a small set of prefix buckets for aggregation.
 * Examples: `unsaved-sql-/.../models/123` -> `unsaved-sql`,
 * `12345:activation:recently-viewed` -> `activation:recently-viewed`,
 * `12345:personalization:foo.bar` -> `personalization:foo.bar`.
 */
export function bucketPrefixForKey(key: string): string {
  if (key.startsWith("unsaved-")) {
    const second = key.slice("unsaved-".length).split("-")[0];
    return second ? `unsaved-${second}` : "unsaved";
  }
  const segments = key.split(":");
  if (segments.length >= 2) {
    // Workspace-scoped keys look like `<workspaceId>:<bucket>:...`. The
    // bucket is the most useful aggregation level.
    return segments.slice(1, 3).join(":") || "_misc";
  }
  return "_misc";
}

export const useDatadog = () => {
  const { user, workspace, slug } = useUser();

  useEffect(() => {
    if (
      user &&
      shouldInitDatadogRum({
        prod: import.meta.env.PROD,
        vercelEnv: import.meta.env.VITE_VERCEL_ENV,
      })
    ) {
      datadogRum.setUser({
        id: user.id.toString(),
        workspace_id: user.current_workspace_id,
        workspace_slug: slug ?? undefined,
        organization_id: user.organization?.id,
      });

      // Tag RUM events with workspace region (matches APM hightouch_region) so
      // view/page-load metrics can be split by region. Read from the reactive
      // workspace context, and clear it when absent, so switching workspace
      // never leaves the previous workspace's region stale.
      if (workspace?.region) {
        datadogRum.setGlobalContextProperty(
          "hightouch_region",
          workspace.region,
        );
      } else {
        datadogRum.removeGlobalContextProperty("hightouch_region");
      }

      // Run the storage audit once per page-load, after the user context is
      // attached so we can correlate quota pressure with workspace.
      if (!auditedThisSession) {
        auditedThisSession = true;
        reportLocalStorageAudit();
      }
    }
  }, [user, workspace, slug]);
};
